/**
 * Livres scolaires et leurs chapitres.
 *
 * Extrait de l'ancien services/api.ts (Lot 8, déplacement pur).
 */

import {
  SchoolBook,
  BookChapter,
} from '../types';
import type { ApiResponse, DataResponse } from '../types/api';
import { ADMIN_URLS } from '../config/adminApi';
import { createApiClient, presentParams, withEnv } from '../lib/http';
import { AppEnvironment } from '../config/environment';

const api = createApiClient();

export class SchoolBooksApiService {
  /** Liste les livres scolaires de l'environnement. */
  static async getSchoolBooks(env: AppEnvironment, filters?: { classId?: string; subjectId?: string }): Promise<SchoolBook[]> {
    const params = presentParams({ classId: filters?.classId, subjectId: filters?.subjectId });
    const response = await api.get<DataResponse<SchoolBook[]>>('/school_books', withEnv(env, { params }));
    return response.data.data;
  }

  /** Lit un livre scolaire par son identifiant. */
  static async getSchoolBookById(env: AppEnvironment, id: string): Promise<SchoolBook> {
    const response = await api.get<DataResponse<SchoolBook>>(`/school_books/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Crée un livre scolaire. */
  static async createSchoolBook(env: AppEnvironment, book: Omit<SchoolBook, 'id'>): Promise<ApiResponse<SchoolBook>> {
    const response = await api.post<ApiResponse<SchoolBook>>(`/school_books`, book, withEnv(env));
    return response.data;
  }

  /** Met à jour un livre scolaire. */
  static async updateSchoolBook(env: AppEnvironment, id: string, book: Partial<SchoolBook>): Promise<ApiResponse<SchoolBook>> {
    const response = await api.put<ApiResponse<SchoolBook>>(`/school_books/${id}`, book, withEnv(env));
    return response.data;
  }

  /** Supprime un livre scolaire. */
  static async deleteSchoolBook(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/school_books/${id}`, withEnv(env));
    return response.data;
  }

  /** Liste les chapitres de livre de l'environnement. */
  static async getBookChapters(env: AppEnvironment, bookId?: string): Promise<BookChapter[]> {
    const params = presentParams({ bookId });
    const response = await api.get<DataResponse<BookChapter[]>>('/book_chapters', withEnv(env, { params }));
    return response.data.data;
  }

  /** Lit un chapitre de livre par son identifiant. */
  static async getBookChapterById(env: AppEnvironment, id: string): Promise<BookChapter> {
    const response = await api.get<DataResponse<BookChapter>>(`/book_chapters/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Crée un chapitre de livre. */
  static async createBookChapter(env: AppEnvironment, chapter: Omit<BookChapter, 'id'>): Promise<ApiResponse<BookChapter>> {
    const response = await api.post<ApiResponse<BookChapter>>(`/book_chapters`, chapter, withEnv(env));
    return response.data;
  }

  /** Met à jour un chapitre de livre. */
  static async updateBookChapter(env: AppEnvironment, id: string, chapter: Partial<BookChapter>): Promise<ApiResponse<BookChapter>> {
    const response = await api.put<ApiResponse<BookChapter>>(`/book_chapters/${id}`, chapter, withEnv(env));
    return response.data;
  }

  /** Supprime un chapitre de livre. */
  static async deleteBookChapter(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/book_chapters/${id}`, withEnv(env));
    return response.data;
  }
}

export default SchoolBooksApiService;
